import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { X, Loader2, History } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useT } from '@/i18n';
import { useBodyScrollLock } from '@/lib/useBodyScrollLock';
import { useTierThresholdsHistory } from '@/features/system-config/system-config.hooks';

/**
 * v0.6.0 N-Q6 safeguard #6 — read-only timeline of historical edits to a
 * payroll-affecting key. Opens from "Ver historial completo" CTA on the
 * SystemConfigPage. Reads /admin/system-config/:key/history (returns up
 * to 100 rows, ordered by changedAt DESC). Each row shows oldValue,
 * newValue, reason, effectiveDate, changedByName.
 */
export interface TierThresholdsHistoryModalProps {
  open: boolean;
  configKey: string;
  onClose: () => void;
}

export function TierThresholdsHistoryModal(props: TierThresholdsHistoryModalProps): JSX.Element {
  useBodyScrollLock(props.open);
  return createPortal(
    <AnimatePresence>
      {props.open ? <Inner {...props} /> : null}
    </AnimatePresence>,
    document.body,
  );
}

function Inner({ configKey, onClose }: TierThresholdsHistoryModalProps): JSX.Element {
  const t = useT();
  const reduce = useReducedMotion();
  const { data, isLoading, error } = useTierThresholdsHistory(configKey);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center p-4 sm:p-8">
      <motion.div
        className="absolute inset-0 bg-nav-navy/50 backdrop-blur-sm"
        initial={reduce ? { opacity: 1 } : { opacity: 0 }}
        animate={{ opacity: 1, transition: { duration: 0.2 } }}
        exit={{ opacity: 0, transition: { duration: 0.15 } }}
        onClick={onClose}
        aria-hidden="true"
      />
      <motion.div
        role="dialog"
        aria-modal="true"
        className="relative bg-white rounded-xl shadow-elevated w-full max-w-2xl max-h-[85vh] overflow-y-auto font-body text-nav-navy"
        initial={reduce ? false : { opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1, transition: { type: 'spring', stiffness: 300, damping: 25 } }}
        exit={{ opacity: 0, scale: 0.98, transition: { duration: 0.15 } }}
      >
        <header className="sticky top-0 bg-white border-b border-nav-card px-5 py-3 flex items-start justify-between gap-3 z-10">
          <div className="flex items-center gap-2.5">
            <History className="w-5 h-5 text-nav-slate shrink-0" aria-hidden />
            <h2 className="font-head text-[15px] font-bold text-nav-navy">
              {t('systemConfig.history.title')} <code className="text-xs bg-nav-hair px-1 py-0.5 rounded">{configKey}</code>
            </h2>
          </div>
          <button type="button" onClick={onClose} aria-label={t('common.close')} className="text-nav-slate hover:text-nav-navy p-1 rounded">
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="p-5">
          {isLoading ? (
            <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin text-nav-slate mx-auto" /></div>
          ) : error ? (
            <p className="text-sm text-nav-red">{(error as Error).message}</p>
          ) : !data || data.items.length === 0 ? (
            <p className="text-sm text-nav-slate text-center py-8">{t('systemConfig.history.empty')}</p>
          ) : (
            <ol className="space-y-3">
              {data.items.map((row) => (
                <li key={row.id} className="rounded-md border border-nav-card bg-nav-softer px-4 py-3">
                  <div className="flex items-baseline justify-between gap-2 flex-wrap">
                    <p className="text-sm font-medium text-nav-navy">
                      {row.changedByName ?? `User #${row.changedByUserId}`}
                    </p>
                    <p className="text-xs text-nav-slate">
                      {new Date(row.changedAt).toLocaleString(undefined, {
                        year: 'numeric', month: 'short', day: 'numeric',
                        hour: 'numeric', minute: '2-digit',
                      })}
                    </p>
                  </div>
                  <p className="text-xs text-nav-slate mt-1">
                    <span className="font-semibold">{t('systemConfig.history.effectiveDate')}:</span>{' '}
                    {row.effectiveDate}
                  </p>
                  <p className="text-xs text-nav-navy mt-2">
                    <span className="font-semibold">{t('systemConfig.history.reason')}:</span> {row.reason}
                  </p>
                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px]">
                    <div className="rounded bg-nav-redTint border border-[#e3b5ae] px-2 py-1">
                      <p className="text-nav-red font-semibold">{t('systemConfig.history.before')}</p>
                      <pre className="font-mono text-nav-red whitespace-pre-wrap break-all">{JSON.stringify(row.oldValue, null, 0)}</pre>
                    </div>
                    <div className="rounded bg-nav-sageTint border border-nav-sage px-2 py-1">
                      <p className="text-nav-sageText font-semibold">{t('systemConfig.history.after')}</p>
                      <pre className="font-mono text-nav-sageText whitespace-pre-wrap break-all">{JSON.stringify(row.newValue, null, 0)}</pre>
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>

        <footer className="sticky bottom-0 bg-white border-t border-nav-card px-5 py-3 flex items-center justify-end">
          <Button type="button" variant="ghost" onClick={onClose}>{t('common.close')}</Button>
        </footer>
      </motion.div>
    </div>
  );
}
